import {
  useRef,
  useState,
  useEffect,
  type RefObject,
  type ChangeEvent,
  type FormEvent,
  type HTMLAttributes,
} from 'react';
import { cn } from '@virginexperiencedays/tailwind';

import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Button } from '../../layout/Button';
import { useQuickBasketContext } from './utils/QuickBasketContext';
import {
  handleClearBasketPromo,
  handleSetBasketPromo,
  type ApiItem,
  type BasketDataApiResponse,
} from '@virginexperiencedays/basket';
import { addToDataLayer } from '../../utils/addToDataLayer';
import { IconQBInfo } from '../../icons/IconQBInfo';
import { IconSuccess } from '../../icons/IconSuccess';
import { IconError } from '../../icons/IconError';

const APP_ENDPOINT = process?.env?.NEXT_PUBLIC_HOME_LINK || '';

export const getItemsFromBasketData = (basketData: BasketDataApiResponse): ApiItem[] => {
  const items = Array.isArray(basketData?.Items) ? basketData.Items : [];
  return items;
};

export type PromoCodeError = {
  readonly message: string;
};

type Props = {
  isPartialDiscount: boolean;
} & HTMLAttributes<HTMLDivElement>;

export function QuickBasketPromo({ isPartialDiscount, ...props }: Readonly<Props>) {
  const {
    handleSetBasketItems,
    basketItems,
    activePromoCode,
    handleSetActivePromoCode,
    setPromoCodeSuccess,
  } = useQuickBasketContext();

  const inputRef = useRef<HTMLInputElement>(null);

  const [promoCodeOpen, setPromoCodeOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState<PromoCodeError | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!basketItems?.length) {
      setPromoCodeError(null);
      setPromoCode('');
    }
  }, [basketItems]);

  useEffect(() => {
    if (basketItems?.length && activePromoCode) {
      handleBasketItemsUpdateWithPromo();
    }
  }, [basketItems, activePromoCode]);

  const handleToggleOpen = () => {
    addToDataLayer({
      event: 'userInteraction',
      eventCategory: 'QB Discount code',
      eventAction: 'Clicked Add a discount code',
      eventLabel: '',
    });
    setPromoCodeOpen((prev) => {
      inputRef?.current?.[prev ? 'blur' : 'focus']?.();
      return !prev;
    });
  };

  const handleChangePromoCode = (e: ChangeEvent<HTMLInputElement>) => {
    setPromoCodeError(null);
    setPromoCode(e.target.value);
  };

  const handleClearPromoCode = async () => {
    setPromoCodeError(null);

    const { success, message, data: basketData } = await handleClearBasketPromo(APP_ENDPOINT);

    if (success) {
      addToDataLayer({
        event: 'userInteraction',
        eventCategory: 'QB Discount code',
        eventAction: 'Clicked remove promo code | Success',
        eventLabel: `promo code - ${activePromoCode}`,
      });
      const items = getItemsFromBasketData(basketData);
      handleSetBasketItems(items);
      setPromoCode('');
      handleSetActivePromoCode('');
    } else {
      setPromoCodeError({ message });
    }
    setPromoCodeSuccess(false);
  };

  const handleSubmitPromoCode = async (e: FormEvent) => {
    e.preventDefault();
    addToDataLayer({
      event: 'userInteraction',
      eventCategory: 'QB Discount code',
      eventAction: 'Clicked Apply promo code',
      eventLabel: '',
    });
    setSubmitting(true);
    const {
      success,
      message,
      data: basketData,
    } = await handleSetBasketPromo(APP_ENDPOINT, promoCode);

    if (success) {
      addToDataLayer({
        event: 'userInteraction',
        eventCategory: 'QB Discount code',
        eventAction: 'Clicked Apply promo code | Success',
        eventLabel: `promo code - ${promoCode}`,
      });
      const items = getItemsFromBasketData(basketData);
      handleSetBasketItems(items);
      handleSetActivePromoCode(promoCode);
    } else {
      addToDataLayer({
        event: 'userInteraction',
        eventCategory: 'QB Discount code',
        eventAction: 'Clicked Apply promo code | Error',
        eventLabel: `INVALID CODE - ${message}`,
      });
      setPromoCodeError({ message });
    }
    setSubmitting(false);
    setPromoCodeSuccess(success);
  };

  const handleBasketItemsUpdateWithPromo = async () => {
    const { success, message } = await handleSetBasketPromo(APP_ENDPOINT, activePromoCode);
    if (success) {
      addToDataLayer({
        event: 'userInteraction',
        eventCategory: 'QB Discount code',
        eventAction: 'Update Apply promo code | Success',
        eventLabel: `promo code - ${activePromoCode}`,
      });
      setPromoCodeSuccess(success);
    } else {
      addToDataLayer({
        event: 'userInteraction',
        eventCategory: 'QB Discount code',
        eventAction: 'Update promo code | Error',
        eventLabel: `INVALID CODE - ${message}`,
      });
      setPromoCodeError({ message });
      handleSetActivePromoCode('');
    }
  };

  return (
    <PromoSummary isOpen={promoCodeOpen} handleIsOpen={handleToggleOpen} {...props}>
      {promoCodeError && !activePromoCode && <PromoError message={promoCodeError.message} />}
      {activePromoCode && isPartialDiscount && (
        <PromoPartial
          type="YOUR DISCOUNT HAS BEEN PART APPLIED"
          message="Some of your items are already at their best possible price, making them non-discountable. We’ve marked the product to which this applies."
          handleClear={handleClearPromoCode}
        />
      )}
      {activePromoCode && !isPartialDiscount && (
        <PromoSuccess promoCode={activePromoCode} handleClear={handleClearPromoCode} />
      )}
      {!activePromoCode && (
        <PromoSubmissionForm
          inputRef={inputRef}
          promoCode={promoCode}
          handleChange={handleChangePromoCode}
          handleSubmit={handleSubmitPromoCode}
          isSubmitting={submitting}
        />
      )}
    </PromoSummary>
  );
}

type PromoSummaryProps = {
  readonly isOpen: boolean;
  readonly handleIsOpen: () => void;
} & HTMLAttributes<HTMLDivElement>;

function PromoSummary({ isOpen, handleIsOpen, children, className, ...props }: PromoSummaryProps) {
  return (
    <div className={cn('mb-4', className)} {...props}>
      <button
        onClick={handleIsOpen}
        className={cn(
          'text-normal text-link flex flex-nowrap items-center gap-1 font-semibold leading-4',
          isOpen && 'mb-4'
        )}
      >
        Add a discount code {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          isOpen ? 'max-h-64' : 'max-h-0'
        )}
        aria-expanded={isOpen}
      >
        <div className="flex flex-col gap-4">{children}</div>
      </div>
    </div>
  );
}

type PromoSubmissionFormProps = {
  inputRef: RefObject<HTMLInputElement>;
  promoCode?: string;
  isSubmitting: boolean;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent) => void;
};

function PromoSubmissionForm({
  inputRef,
  promoCode,
  isSubmitting,
  handleChange,
  handleSubmit,
}: Readonly<PromoSubmissionFormProps>) {
  const handleInputClickTracking = () => {
    addToDataLayer({
      event: 'userInteraction',
      eventCategory: 'QB Discount code',
      eventAction: 'Clicked Input field',
      eventLabel: promoCode,
    });
  };
  return (
    <form onSubmit={handleSubmit} className="flex flex-nowrap gap-2">
      <input
        ref={inputRef}
        type="text"
        value={promoCode}
        onChange={handleChange}
        onFocus={handleInputClickTracking}
        placeholder="Enter discount code"
        className="border-border-neutral h-12 w-full rounded-md border p-4 outline-none"
      />
      <Button
        variant="primary"
        disabled={!promoCode || isSubmitting}
        className="text-base leading-4"
      >
        Apply
      </Button>
    </form>
  );
}

function PromoError({ message }: PromoCodeError) {
  return (
    <div className="bg-background-primary-faded grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 rounded-md border border-[#FFBFC1] p-4">
      <IconError />
      <p className="col-start-2 leading-5">
        <div dangerouslySetInnerHTML={{ __html: message }} />
      </p>
    </div>
  );
}

function PromoPartial({
  type,
  message,
  handleClear,
}: Readonly<{
  type: string;
  message: string;
  handleClear: () => void;
}>) {
  return (
    <div className="mt-3.5 grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 rounded-md border border-[#FEF08A] bg-[#FEF9C3] p-4">
      <IconQBInfo />
      <div className="mb-1 flex items-start gap-4">
        <p className="font-bold uppercase">{type}</p>
        <button onClick={handleClear} className="text-neutral-faded underline">
          Remove
        </button>
      </div>
      <p className="col-start-2 leading-5">{message}</p>
    </div>
  );
}

type PromoSuccessProps = {
  promoCode: string;
  handleClear: () => void;
};

function PromoSuccess({ promoCode, handleClear }: Readonly<PromoSuccessProps>) {
  return (
    <div className="flex flex-nowrap justify-between gap-4 rounded-md border border-[#34A3E7] bg-[#CCE5FF] p-4">
      <div className="flex flex-nowrap items-center gap-4">
        <IconSuccess />
        <p>
          Code applied: <strong>{promoCode}</strong>
        </p>
      </div>
      <button onClick={handleClear} className="text-neutral-faded underline">
        Remove
      </button>
    </div>
  );
}
